import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import MenuBar from "../common/MenuPanel";
import FooterPanel from "../common/FooterPanel";
import LoadingPanel from "../common/LoadingPanel";
import EventDetailScreen from "./EventDetailScreen";
import EventBrochurePopup from "./EventBrochurePopup";
import {
  buildGuestRegistrationLink,
  buildQrImageUrl,
  createEvent,
  deleteEvent,
  getEventById,
  getEvents,
  getEventRegistrationIds,
  getEventSummary,
  updateEvent,
  uploadSpecialEventBookingsXls,
  uploadEventBrochure,
} from "../../services/EventMasterService";
import { EVENT_TYPE_OPTIONS, EVENT_STATUS_OPTIONS } from "../common/Constants";

function normalizeEventType(type) {
  const incoming = (type || "").trim();
  if (!incoming) return "Retreat";
  return EVENT_TYPE_OPTIONS.includes(incoming) ? incoming : "Retreat";
}

function getOffsetDateString(offsetDays = 0) {
  const date = new Date();
  date.setDate(date.getDate() + Number(offsetDays || 0));
  return date.toISOString().slice(0, 10);
}

function createDefaultAgeGroup() {
  return {
    ageGroupName: "General",
    minAge: 0,
    maxAge: 120,
    rate: 0,
    currency: "INR",
    isActive: true,
    maxCount: "",
    waitlistCount: "",
    _key: `ag-${Date.now()}-${Math.random()}`,
  };
}

function createDefaultPaymentMode() {
  return {
    modeType: "Cash",
    displayName: "Cash",
    modeAttributes: { instructions: "" },
    isActive: true,
    _key: `pm-${Date.now()}-${Math.random()}`,
  };
}

function ensureKeys(items = []) {
  return items.map((item) => ({
    ...item,
    _key: item._key || `${Date.now()}-${Math.random()}`,
  }));
}

function stripKeys(items = []) {
  return items.map(({ _key, ...rest }) => rest);
}

function eventToForm(event) {
  return {
    name: event?.name || "",
    type: normalizeEventType(event?.type),
    status: event?.status || "Draft",
    regDate: event?.regDate || "",
    regEndDate: event?.regEndDate || "",
    startDate: event?.startDate || "",
    endDate: event?.endDate || "",
    shortDescription: event?.shortDescription || "",
    description: event?.description || "",
    timings: event?.timings || "",
    location: event?.location || "",
    address: event?.address || "",
    socialMediaLink: event?.socialMediaLink || "",
    map: event?.map || "",
    wog: event?.wog || "",
    retreatEmail: event?.retreatEmail || "",
    maxRegistrationCount: event?.maxRegistrationCount ?? "",
    waitlistCount: event?.waitlistCount ?? "",
    contact: event?.contact || "",
    onlineRegRequired: Boolean(event?.onlineRegRequired),
    emailsEnabled: event?.emailsEnabled === undefined ? true : Boolean(event?.emailsEnabled),
    ageGroups: ensureKeys(
      event?.ageGroups?.length ? event.ageGroups : [createDefaultAgeGroup()]
    ),
    paymentModes: ensureKeys(
      event?.paymentModes?.length ? event.paymentModes : [createDefaultPaymentMode()]
    ),
  };
}

const defaultForm = eventToForm(null);

function EventMasterScreen() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [events, setEvents] = useState([]);
  const [selectedID, setSelectedID] = useState(null);
  const [screenView, setScreenView] = useState("list");
  const [mode, setMode] = useState("view");
  const [form, setForm] = useState(defaultForm);

  const [filterName, setFilterName] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterFromDate, setFilterFromDate] = useState(getOffsetDateString(-30));
  const [filterToDate, setFilterToDate] = useState(getOffsetDateString(200));

  const [eventSummaries, setEventSummaries] = useState({});
  const [brochureModalEvent, setBrochureModalEvent] = useState(null);
  const specialBookingUploadRef = useRef(null);
  const [specialBookingUploadEvent, setSpecialBookingUploadEvent] = useState(null);

  const selectedEvent = useMemo(
    () => events.find((event) => String(event.eventID) === String(selectedID)) || null,
    [events, selectedID]
  );

  const isEditing = mode === "create" || mode === "edit";

  async function loadEvents(filters) {
    const response = await getEvents(filters);
    setEvents(response.events || []);
    return response.events || [];
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const loaded = await loadEvents({
          name: filterName,
          type: filterType,
          fromDate: filterFromDate,
          toDate: filterToDate,
        });

        if (!cancelled && loaded.length > 0) {
          setSelectedID((currentID) => currentID || loaded[0].eventID);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || "Unable to load events.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // Initial screen load should run once with default filter state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedID) return;
    let cancelled = false;

    async function fetchSummary() {
      try {
        const response = await getEventSummary(selectedID);
        if (!cancelled) {
          setEventSummaries((current) => ({
            ...current,
            [selectedID]: response.summary,
          }));
        }
      } catch (_) {
        if (!cancelled) {
          setEventSummaries((current) => ({
            ...current,
            [selectedID]: {
              total: 0,
              registered: 0,
              waitlist: 0,
              checkedIn: 0,
              checkInComplete: 0,
              cancelled: 0,
            },
          }));
        }
      }
    }

    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [selectedID]);

  function setField(name, value) {
    setForm((current) => ({
      ...current,
      [name]: name === "type" ? normalizeEventType(value) : value,
    }));
  }

  function toApiPayload(raw) {
    return {
      ...raw,
      type: normalizeEventType(raw.type),
      ageGroups: stripKeys(raw.ageGroups),
      paymentModes: stripKeys(raw.paymentModes),
    };
  }

  function setAgeField(index, name, value) {
    setForm((current) => ({
      ...current,
      ageGroups: ensureKeys(
        current.ageGroups.map((item, i) =>
          i === index ? { ...item, [name]: value } : item
        )
      ),
    }));
  }

  function addAgeGroup() {
    setForm((current) => ({
      ...current,
      ageGroups: ensureKeys([...current.ageGroups, createDefaultAgeGroup()]),
    }));
  }

  function removeAgeGroup(index) {
    setForm((current) => ({
      ...current,
      ageGroups:
        current.ageGroups.length === 1
          ? [createDefaultAgeGroup()]
          : ensureKeys(current.ageGroups.filter((_, i) => i !== index)),
    }));
  }

  function setPaymentModeField(index, name, value) {
    setForm((current) => ({
      ...current,
      paymentModes: ensureKeys(
        current.paymentModes.map((item, i) =>
          i === index ? { ...item, [name]: value } : item
        )
      ),
    }));
  }

  function setPaymentModeAttribute(index, attrName, attrValue) {
    setForm((current) => ({
      ...current,
      paymentModes: ensureKeys(
        current.paymentModes.map((item, i) =>
          i === index
            ? {
                ...item,
                modeAttributes: {
                  ...(item.modeAttributes || {}),
                  [attrName]: attrValue,
                },
              }
            : item
        )
      ),
    }));
  }

  function addPaymentMode() {
    setForm((current) => ({
      ...current,
      paymentModes: ensureKeys([
        ...current.paymentModes,
        createDefaultPaymentMode(),
      ]),
    }));
  }

  function removePaymentMode(index) {
    setForm((current) => ({
      ...current,
      paymentModes:
        current.paymentModes.length === 1
          ? [createDefaultPaymentMode()]
          : ensureKeys(current.paymentModes.filter((_, i) => i !== index)),
    }));
  }

  async function handleApplyFilters() {
    if (filterFromDate && filterToDate && filterFromDate > filterToDate) {
      setError("From date must be on or before To date.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const loaded = await loadEvents({
        name: filterName,
        type: filterType,
        fromDate: filterFromDate,
        toDate: filterToDate,
      });
      if (loaded.length > 0) {
        setSelectedID(loaded[0].eventID);
      } else {
        setSelectedID(null);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to filter events.");
    } finally {
      setLoading(false);
    }
  }

  async function handleClearFilters() {
    setFilterName("");
    setFilterType("");
    setFilterFromDate(getOffsetDateString(-5));
    setFilterToDate(getOffsetDateString(200));
    setLoading(true);
    setError("");
    try {
      const loaded = await loadEvents({
        fromDate: getOffsetDateString(-5),
        toDate: getOffsetDateString(30),
      });
      setSelectedID(loaded[0]?.eventID || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to clear filters.");
    } finally {
      setLoading(false);
    }
  }

  function handleOpenDetail(id = selectedID) {
    if (!id) return;
    setSelectedID(id);
    setMode("view");
    setScreenView("detail");
  }

  function handleCreate() {
    setForm(eventToForm(null));
    setMode("create");
    setScreenView("detail");
    setSelectedID(null);
  }

  function handleCopy(targetEvent = selectedEvent) {
    if (!targetEvent) return;
    setForm(eventToForm({ ...targetEvent, name: `${targetEvent.name} (Copy)` }));
    setMode("create");
    setScreenView("detail");
  }

  async function handleModify(targetEvent = selectedEvent) {
    const eventID = targetEvent?.eventID || selectedID;
    if (!eventID) return;

    setLoading(true);
    setError("");
    try {
      const response = await getEventById(eventID);
      const fullEvent = response?.event || targetEvent;
      if (!fullEvent) return;

      setForm(eventToForm(fullEvent));
      setMode("edit");
      setScreenView("detail");
      setSelectedID(fullEvent.eventID || eventID);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load event details for edit.");
    } finally {
      setLoading(false);
    }
  }

  function handleBackToList() {
    setMode("view");
    setScreenView("list");
  }

  async function handleSave(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNotice("");

    try {
      if (form.regDate && form.regEndDate && form.regEndDate < form.regDate) {
        throw new Error("Registration end date cannot be before registration start date.");
      }
      if (form.startDate && form.endDate && form.endDate < form.startDate) {
        throw new Error("Event end date cannot be before event start date.");
      }

      const payload = toApiPayload(form);

      const response =
        mode === "create"
          ? await createEvent(payload)
          : await updateEvent(selectedEvent.eventID, payload);

      const savedEvent = response.event;
      setNotice(
        mode === "create"
          ? "Event created successfully."
          : "Event updated successfully."
      );

      const latestList = await loadEvents({
        name: filterName,
        type: filterType,
        fromDate: filterFromDate,
        toDate: filterToDate,
      });

      const latestSaved =
        latestList.find(
          (item) => String(item.eventID) === String(savedEvent?.eventID)
        ) || savedEvent;

      setSelectedID(latestSaved?.eventID || null);
      setMode("view");
      setScreenView("detail");
      if (latestSaved?.eventID) {
        const fullEvent = await getEventById(latestSaved.eventID);
        setForm(eventToForm(fullEvent.event));
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Unable to save event.");
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleStatus(targetEvent = selectedEvent) {
    if (!targetEvent) return;
    const nextStatus = targetEvent.status === "Confirmed" ? "Closed" : "Confirmed";
    setLoading(true);
    setError("");
    try {
      await updateEvent(targetEvent.eventID, {
        ...toApiPayload(targetEvent),
        status: nextStatus,
      });
      await loadEvents({
        name: filterName,
        type: filterType,
        fromDate: filterFromDate,
        toDate: filterToDate,
      });
      setNotice(`Event marked as ${nextStatus}.`);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to update status.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(targetEvent = selectedEvent) {
    if (!targetEvent) return;
    if (!window.confirm(`Delete event ${targetEvent.name}?`)) return;

    setLoading(true);
    setError("");
    try {
      await deleteEvent(targetEvent.eventID);
      const loaded = await loadEvents({
        name: filterName,
        type: filterType,
        fromDate: filterFromDate,
        toDate: filterToDate,
      });
      setSelectedID(loaded[0]?.eventID || null);
      setScreenView("list");
      setNotice("Event deleted successfully.");
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to delete event.");
    } finally {
      setLoading(false);
    }
  }

  async function handleUploadBrochure(eventID, file) {
    setLoading(true);
    setError("");
    try {
      const response = await uploadEventBrochure(eventID, file);
      setNotice("Brochure uploaded successfully.");
      setBrochureModalEvent(response.event);
      await loadEvents({
        name: filterName,
        type: filterType,
        fromDate: filterFromDate,
        toDate: filterToDate,
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to upload brochure.");
      throw err;
    } finally {
      setLoading(false);
    }
  }

  function handleUploadSpecialBookings(targetEvent = selectedEvent) {
    if (!targetEvent?.eventID) {
      setError("Please select an event before uploading special bookings.");
      return;
    }
    setSpecialBookingUploadEvent(targetEvent);
    specialBookingUploadRef.current?.click();
  }

  async function handleSpecialBookingsFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const targetEvent = specialBookingUploadEvent || selectedEvent;
    if (!targetEvent?.eventID) {
      setError("No target event selected for special booking upload.");
      event.target.value = "";
      return;
    }

    setLoading(true);
    setError("");
    setNotice("");
    try {
      const response = await uploadSpecialEventBookingsXls(
        targetEvent.eventID,
        file,
        targetEvent.type || "Leaders"
      );
      setNotice(
        `Special booking upload complete. Inserted: ${response?.inserted || 0}, Skipped: ${response?.skipped || 0}.`
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to upload special booking XLS.");
    } finally {
      setLoading(false);
      setSpecialBookingUploadEvent(null);
      event.target.value = "";
    }
  }

  async function handleCopyGuestLink(targetEvent = selectedEvent) {
    if (!targetEvent?.eventID) return;
    const link = buildGuestRegistrationLink(targetEvent.eventID);
    try {
      await navigator.clipboard.writeText(link);
      setNotice("Guest registration link copied.");
    } catch {
      setError("Unable to copy guest link. Please copy it manually from QR dialog.");
    }
  }

  function handlePrintEvent(targetEvent = selectedEvent) {
    if (!targetEvent) return;
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=900,height=700");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>${targetEvent.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            h1 { margin: 0 0 8px; }
            .meta { color: #555; margin-bottom: 16px; }
            .section { margin-bottom: 14px; }
            .label { font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>${targetEvent.name}</h1>
          <div class="meta">${targetEvent.startDate || ""} to ${targetEvent.endDate || ""}</div>
          <div class="section"><span class="label">Type:</span> ${targetEvent.type || "-"}</div>
          <div class="section"><span class="label">Status:</span> ${targetEvent.status || "-"}</div>
          <div class="section"><span class="label">Location:</span> ${targetEvent.location || "-"}</div>
          <div class="section"><span class="label">Address:</span> ${targetEvent.address || "-"}</div>
          <div class="section"><span class="label">Description:</span><br/>${targetEvent.description || ""}</div>
          <div class="section"><span class="label">Guest Link:</span> ${buildGuestRegistrationLink(targetEvent.eventID)}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  function handlePrintQrCode(targetEvent = selectedEvent) {
    if (!targetEvent?.eventID) return;
    const link = buildGuestRegistrationLink(targetEvent.eventID);
    const qrUrl = buildQrImageUrl(link);
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=500,height=700");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>${targetEvent.name} QR</title>
          <style>
            body { font-family: Arial, sans-serif; display:flex; flex-direction:column; align-items:center; padding:24px; }
            img { width: 320px; height: 320px; margin: 8px 0 12px; }
            .name { font-size: 20px; font-weight: 700; margin-bottom: 8px; }
            .link { font-size: 12px; word-break: break-all; text-align:center; }
          </style>
        </head>
        <body>
          <div class="name">${targetEvent.name}</div>
          <img src="${qrUrl}" alt="Event guest registration QR" />
          <div class="link">${link}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  async function handlePrintRegistrationIds(targetEvent = selectedEvent, gender = "all") {
    if (!targetEvent?.eventID) return;
    setLoading(true);
    setError("");
    try {
      const response = await getEventRegistrationIds(targetEvent.eventID, gender);
      const labels = response.labels || [];
      if (labels.length === 0) {
        setNotice("No registration IDs found for the selected filter.");
        return;
      }

      const printWindow = window.open("", "_blank", "noopener,noreferrer,width=1200,height=900");
      if (!printWindow) return;

      const labelsHtml = labels
        .map(
          (label) => `
            <div class="label-card">
              <div class="title">${targetEvent.name}</div>
              <div class="pid">${label.participantID}</div>
              <div class="name">${label.name || "-"}</div>
              <div class="meta">${label.gender || ""} ${label.relation ? `| ${label.relation}` : ""}</div>
            </div>
          `
        )
        .join("");

      printWindow.document.write(`
        <html>
          <head>
            <title>${targetEvent.name} IDs</title>
            <style>
              @page { size: A4; margin: 8mm; }
              body { margin: 0; font-family: Arial, sans-serif; }
              .sheet { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6mm; }
              .label-card { border: 1px solid #aaa; border-radius: 4px; padding: 6px; min-height: 32mm; }
              .title { font-size: 10px; color: #555; margin-bottom: 3px; }
              .pid { font-size: 14px; font-weight: 700; margin-bottom: 4px; }
              .name { font-size: 12px; margin-bottom: 3px; }
              .meta { font-size: 10px; color: #666; }
            </style>
          </head>
          <body>
            <div class="sheet">${labelsHtml}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to print registration IDs.");
    } finally {
      setLoading(false);
    }
  }

  const canCreate = true;
  const canEdit = true;
  const canDelete = true;

  if (loading && screenView === "list" && events.length === 0) {
    return (
      <>
        <div className="container-fluid position-relative p-0">
          <MenuBar />
        </div>
        <LoadingPanel />
      </>
    );
  }

  return (
    <>
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>

      <div className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">Event Management</h1>
          </div>
        </div>
      </div>

      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {notice}
          </Alert>
        )}

        {screenView === "list" ? (
          <Paper elevation={2} sx={{ p: 2 }}>

            <style>
              {`
                .tooltip-wrap { position: relative; display: inline-block; width:100%; }
                .tooltip-text {
                  display: none;
                  position: absolute;
                  bottom: calc(100% + 8px);
                  left: 50%;
                  transform: translateX(-50%);
                  background: rgba(0,0,0,0.85);
                  color: #fff;
                  padding: 8px 10px;
                  border-radius: 6px;
                  font-size: 12px;
                  white-space: nowrap;
                  z-index: 2000;
                  max-width: 260px;
                  text-align: center;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                }
                .tooltip-wrap:focus-within .tooltip-text { display:block; }
                .tooltip-text::after {
                  content: '';
                  position: absolute;
                  top: 100%;
                  left: 50%;
                  transform: translateX(-50%);
                  border-width: 6px;
                  border-style: solid;
                  border-color: rgba(0,0,0,0.85) transparent transparent transparent;
                }
              `}
            </style>

            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
              <div className="flex-grow-1" style={{ minWidth: "220px" }}>
                <div className="form-group mb-0 tooltip-wrap">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Event Name"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    aria-describedby="help-event-name"
                  />
                  <div className="tooltip-text" id="help-event-name" role="status">
                    Enter full or partial event name.
                  </div>
                </div>
              </div>

              <div className="flex-grow-1" style={{ minWidth: "170px" }}>
                <div className="form-group mb-0 tooltip-wrap">
                  <select
                    className="form-control"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    aria-describedby="help-event-type"
                  >
                    <option value="">All Types</option>
                    {EVENT_TYPE_OPTIONS.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <div className="tooltip-text" id="help-event-type" role="status">
                    Select an event type.
                  </div>
                </div>
              </div>

              <div className="flex-grow-1" style={{ minWidth: "160px" }}>
                <div className="form-group mb-0 tooltip-wrap">
                  <input
                    type="date"
                    className="form-control"
                    value={filterFromDate}
                    onChange={(e) => setFilterFromDate(e.target.value)}
                    aria-describedby="help-from-date"
                  />
                  <div className="tooltip-text" id="help-from-date" role="status">
                    Show events starting on or after this date.
                  </div>
                </div>
              </div>

              <div className="flex-grow-1" style={{ minWidth: "160px" }}>
                <div className="form-group mb-0 tooltip-wrap">
                  <input
                    type="date"
                    className="form-control"
                    value={filterToDate}
                    onChange={(e) => setFilterToDate(e.target.value)}
                    aria-describedby="help-to-date"
                  />
                  <div className="tooltip-text" id="help-to-date" role="status">
                    Show events starting on or before this date.
                  </div>
                </div>
              </div>
            </div>

            <div
              className="row mt-1 mb-3 justify-content-center"
              style={{
                border: "1px solid rgb(145, 86, 19)",
                borderRadius: "5px",
                padding: "6px",
              }}
            >

              <div className="d-flex flex-wrap gap-2 justify-content-center mb-1">
                <button
                  className="btn btn-primary btn-sm d-flex align-items-center justify-content-center gap-2"
                  onClick={handleApplyFilters}
                  title="View filtered events"
                >
                  <i className="fas fa-list"></i>
                  List
                </button>

                <button
                  className="btn btn-secondary btn-sm d-flex align-items-center justify-content-center gap-2"
                  onClick={handleClearFilters}
                  title="Clear all filters"
                >
                  <i className="fas fa-eraser"></i>
                  Clear
                </button>

                <button
                  className="btn btn-success btn-sm d-flex align-items-center justify-content-center gap-2"
                  onClick={handleCreate}
                  title="Create a new event"
                >
                  <i className="fas fa-plus-circle"></i>
                  Create Event
                </button>
              </div>
            </div>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Registrations</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event) => {
                  const summary = eventSummaries[event.eventID] || {
                    total: 0,
                    registered: 0,
                    waitlist: 0,
                    cancelled: 0,
                  };
                  return (
                    <TableRow key={event.eventID} hover>
                      <TableCell>{event.name}</TableCell>
                      <TableCell>{event.type || "-"}</TableCell>
                      <TableCell>
                        {event.startDate} to {event.endDate}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={event.status || "Draft"} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          <Chip size="small" label={`Total: ${summary.total || 0}`} />
                          <Chip
                            size="small"
                            color="success"
                            label={`Reg: ${summary.registered || 0}`}
                          />
                          <Chip
                            size="small"
                            color="warning"
                            label={`Wait: ${summary.waitlist || 0}`}
                          />
                          <Chip
                            size="small"
                            color="error"
                            label={`Can: ${summary.cancelled || 0}`}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedID(event.eventID);
                            setForm(eventToForm(event));
                            handleOpenDetail(event.eventID);
                          }}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        ) : (
          <EventDetailScreen
            isEditing={isEditing}
            selectedEvent={selectedEvent}
            registrationSummary={selectedID ? eventSummaries[selectedID] : null}
            mode={mode}
            loading={loading}
            canCreate={canCreate}
            canEdit={canEdit}
            canDelete={canDelete}
            form={form}
            typeOptions={EVENT_TYPE_OPTIONS}
            statusOptions={EVENT_STATUS_OPTIONS}
            onBackToList={handleBackToList}
            onCopy={handleCopy}
            onModify={handleModify}
            onToggleStatus={handleToggleStatus}
            onOpenBrochure={setBrochureModalEvent}
            onUploadSpecialBookings={handleUploadSpecialBookings}
            onPrintEvent={handlePrintEvent}
            onPrintQrCode={handlePrintQrCode}
            onPrintRegistrationIds={handlePrintRegistrationIds}
            onCopyGuestLink={handleCopyGuestLink}
            onDelete={handleDelete}
            onCancel={handleBackToList}
            onSubmit={handleSave}
            onFieldChange={setField}
            onAgeFieldChange={setAgeField}
            onAddAgeGroup={addAgeGroup}
            onRemoveAgeGroup={removeAgeGroup}
            onPaymentModeFieldChange={setPaymentModeField}
            onPaymentModeAttributeChange={setPaymentModeAttribute}
            onAddPaymentMode={addPaymentMode}
            onRemovePaymentMode={removePaymentMode}
          />
        )}
      </Box>

      <input
        ref={specialBookingUploadRef}
        type="file"
        accept=".xls,.xlsx"
        style={{ display: "none" }}
        onChange={handleSpecialBookingsFileChange}
      />

      <EventBrochurePopup
        event={brochureModalEvent}
        loading={loading}
        canEdit={canEdit}
        onClose={() => setBrochureModalEvent(null)}
        onUpload={handleUploadBrochure}
        onError={setError}
      />

      <FooterPanel />
    </>
  );
}

export default EventMasterScreen;
